// CreateMemberAlcoholCannabis.jsx
import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import i18n from "@/i18n";
import cls from "./CreateMemberAlcoholCannabis.module.scss";
import { ALCOHOL_CANNABIS } from "@/enums/lifestyleEnums";
import { useAuth } from "@/app/auth-context-state";

/* Welcome-style hero placeholders (reuse q12 for now) */
import mrImg from "@/assets/UI/memberCreate/q16Mr.webp";
import mrsImg from "@/assets/UI/memberCreate/q16Mrs.webp";
import mrMImg from "@/assets/UI/memberCreate/q16MrM.webp";
import mrsMImg from "@/assets/UI/memberCreate/q16MrsM.webp";

/* After-form cards images (reuse existing assets for now) */
import fitAndActionImg from "@/assets/UI/memberCreate/q16A.webp";
import curvyImg from "@/assets/UI/memberCreate/q16B.webp";
import naturalImg from "@/assets/UI/memberCreate/q16D.webp";
import athleticImg from "@/assets/UI/memberCreate/q16C.webp";

/* ---- Framer animations (same feel as SexualRole) ---- */
const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

/* Outlet context hook */
function useMemberLayoutContext() {
  return useOutletContext();
}

/* Emoji meta */
const META = {
  alcohol_only: "🍹",
  cannabis_only: "🌿",
  both: "🍸",
  none: "🚫",
};

/* Cards */
const CARD_KEYS_FALLBACK = ["alcohol_only", "cannabis_only", "both", "none"];

const CARD_IMAGES = {
  alcohol_only: fitAndActionImg,
  cannabis_only: curvyImg,
  both: athleticImg,
  none: naturalImg,
};

function getClosingKey({ isCouple, rawGender }) {
  if (isCouple) return "onboarding.alcohol_cannabis.closing.couple";

  if (rawGender === "man" || rawGender === "trans_man") {
    return "onboarding.alcohol_cannabis.closing.man";
  }
  if (rawGender === "woman" || rawGender === "trans_woman") {
    return "onboarding.alcohol_cannabis.closing.woman";
  }
  return "onboarding.alcohol_cannabis.closing.other";
}

export default function CreateMemberAlcoholCannabis() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const layoutCtx = useMemberLayoutContext() || {};
  const { memberType, isCouple: isCoupleCtx, rawGender } = layoutCtx;
  const isCouple = Boolean(isCoupleCtx || memberType === "couple");

  const [submitting, setSubmitting] = useState(false);
  const [memberChoice, setMemberChoice] = useState("");
  const [partnerChoice, setPartnerChoice] = useState("");

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const t = (k, o) => i18n.t(k, o);

  const title = t("onboarding.alcohol_cannabis.title", {
    defaultValue: "Alcohol & cannabis",
  });

  const intro1 = t("onboarding.alcohol_cannabis.intro1", {
    defaultValue:
      "This helps us suggest nights and matches that fit your comfort level.",
  });
  const intro2 = t("onboarding.alcohol_cannabis.intro2", {
    defaultValue: "No judgement. Just clarity.",
  });

  const subtitle = !isCouple
    ? t("onboarding.alcohol_cannabis.single.lead", {
        defaultValue: "What’s your vibe when it comes to alcohol or cannabis",
      })
    : t("onboarding.alcohol_cannabis.couple.lead", {
        defaultValue: "Pick one for you, and one for your partner.",
      });

  const coupleDetail = isCouple
    ? t("onboarding.alcohol_cannabis.couple.detail", {
        defaultValue:
          "We use this to keep invites and event suggestions aligned for both of you.",
      })
    : "";

  const partnerLabel = t("onboarding.alcohol_cannabis.partner.label", {
    defaultValue: "Now pick your partner’s vibe",
  });

  const hintFallback =
    t("onboarding.alcohol_cannabis.tapToSelect", {
      defaultValue: "Tap to select what matches your vibe ✨",
    }) || "Tap to select";

  const values =
    Array.isArray(ALCOHOL_CANNABIS) && ALCOHOL_CANNABIS.length
      ? ALCOHOL_CANNABIS
      : CARD_KEYS_FALLBACK;

  const options = values.map((value) => {
    const labelKey = `onboarding.alcohol_cannabis.options.${value}.label`;
    const hintKey = `onboarding.alcohol_cannabis.options.${value}.hint`;

    return {
      value,
      emoji: META[value] || "✨",
      title: t(labelKey, { defaultValue: value }),
      hint: t(hintKey, { defaultValue: "" }) || hintFallback,
    };
  });

  const cardKeys = values.length ? values : CARD_KEYS_FALLBACK;

  const cards = cardKeys.map((key) => ({
    key,
    img: CARD_IMAGES[key] || mrImg,
    emoji: META[key] || "✨",
    title: t(`onboarding.alcohol_cannabis.cards.${key}.title`, {
      defaultValue: t(`onboarding.alcohol_cannabis.options.${key}.label`, {
        defaultValue: key,
      }),
    }),
    desc: t(`onboarding.alcohol_cannabis.cards.${key}.desc`, {
      defaultValue: "",
    }),
  }));

  const closingKey =
    memberChoice && (!isCouple || partnerChoice)
      ? getClosingKey({ isCouple, rawGender })
      : null;

  const closingText = closingKey ? t(closingKey, { defaultValue: "" }) : "";

  async function onSubmit(e) {
    e.preventDefault();

    if (!memberChoice) {
      toast.info(
        t("onboarding.alcohol_cannabis.toast.member_required", {
          defaultValue: "Please pick one for you.",
        }),
      );
      return;
    }

    if (isCouple && !partnerChoice) {
      toast.info(
        t("onboarding.alcohol_cannabis.toast.partner_required", {
          defaultValue: "Please pick one for your partner.",
        }),
      );
      return;
    }

    const payload = isCouple
      ? {
          memberAlcoholCannabis: memberChoice,
          partnerAlcoholCannabis: partnerChoice,
        }
      : {
          memberAlcoholCannabis: memberChoice,
        };

    setSubmitting(true);
    try {
      const { data } = await api.post(
        "/v1/member-create/alcohol_cannabis",
        payload,
        { skipToast: true },
      );

      await refreshMe();
      navigate(data?.next || "/member/be_matched", { replace: true });
    } catch (err) {
      console.error("update alcohol/cannabis failed:", err);
      toast.error(
        t("onboarding.alcohol_cannabis.toast.save_error", {
          defaultValue: "Something went wrong.",
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  const submitDisabled =
    submitting || !memberChoice || (isCouple && !partnerChoice);

  return (
    <main className={cls.page}>
      {/* Fixed background stage */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      {/* Single-page scroll content (no nested scroll) */}
      <div className={cls.pageInner}>
        <div className={cls.formCard}>
          <div className={cls.formTop}>
            <div className={cls.formMeta}>
              <div className={cls.formTitle}>{title}</div>
              <div className={cls.formSub}>{intro1}</div>
              <div className={cls.formSub}>{intro2}</div>

              <div className={cls.formSub} style={{ marginTop: 8 }}>
                {subtitle}
              </div>

              {isCouple && coupleDetail ? (
                <div className={cls.formSub} style={{ marginTop: 8 }}>
                  {coupleDetail}
                </div>
              ) : null}
            </div>
          </div>

          <form className={cls.form} onSubmit={onSubmit} noValidate>
            {/* Member options */}
            <fieldset className={cls.options}>
              <legend className="sr-only">{subtitle}</legend>

              {options.map((opt) => {
                const checked = memberChoice === opt.value;

                return (
                  <label
                    key={`m-${opt.value}`}
                    className={`${cls.option} ${checked ? cls.optionActive : ""}`}
                  >
                    <input
                      type="radio"
                      name="memberAlcoholCannabis"
                      value={opt.value}
                      checked={checked}
                      onChange={() => setMemberChoice(opt.value)}
                    />

                    <span className={cls.optionBody}>
                      <span className={cls.optionEmoji} aria-hidden="true">
                        {opt.emoji}
                      </span>

                      <span className={cls.optionText}>
                        <strong>{opt.title}</strong>
                        <small>{opt.hint}</small>
                      </span>

                      <span className={cls.optionDot} aria-hidden="true" />
                    </span>
                  </label>
                );
              })}
            </fieldset>

            {/* Partner options (couple only) */}
            {isCouple && (
              <div className={cls.partnerBlock}>
                <div className={cls.partnerTitle}>{partnerLabel}</div>

                <fieldset className={cls.options}>
                  <legend className="sr-only">{partnerLabel}</legend>

                  {options.map((opt) => {
                    const checked = partnerChoice === opt.value;

                    return (
                      <label
                        key={`p-${opt.value}`}
                        className={`${cls.option} ${
                          checked ? cls.optionActive : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="partnerAlcoholCannabis"
                          value={opt.value}
                          checked={checked}
                          onChange={() => setPartnerChoice(opt.value)}
                        />

                        <span className={cls.optionBody}>
                          <span className={cls.optionEmoji} aria-hidden="true">
                            {opt.emoji}
                          </span>

                          <span className={cls.optionText}>
                            <strong>{opt.title}</strong>
                            <small>{opt.hint}</small>
                          </span>

                          <span className={cls.optionDot} aria-hidden="true" />
                        </span>
                      </label>
                    );
                  })}
                </fieldset>
              </div>
            )}

            {closingText ? (
              <div className={cls.closingNote} aria-live="polite">
                {closingText.split("\n").map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            ) : null}

            {/* Submit */}
            <div className={cls.actions}>
              <button
                type="submit"
                className={cls.submit}
                disabled={submitDisabled}
                aria-busy={submitting ? "true" : "false"}
              >
                {submitting
                  ? t("common.saving", { defaultValue: "Saving…" })
                  : t("common.continue", { defaultValue: "Continue" })}
              </button>
            </div>
          </form>

          {/* After-form cards */}
          <section
            className={cls.cardsSection}
            aria-label="Alcohol and cannabis guide cards"
          >
            <div className={cls.cardsHeader}>
              <div className={cls.cardsTitle}>
                {t("onboarding.alcohol_cannabis.guide.title", {
                  defaultValue: "Quick guide (if you’re curious)",
                })}
              </div>
              <div className={cls.cardsSub}>
                {t("onboarding.alcohol_cannabis.guide.body", {
                  defaultValue:
                    "Pick what feels right above. These are simple explanations to make it easier.",
                })}
              </div>
            </div>

            <div className={cls.cardsGrid}>
              {cards.map((c) => (
                <article key={c.key} className={cls.gCard}>
                  <div className={cls.gCardMedia}>
                    <img
                      className={cls.gCardImg}
                      src={c.img}
                      alt=""
                      loading="lazy"
                      draggable="false"
                    />
                    <div className={cls.gCardTint} />
                    <div className={cls.gCardIcon} aria-hidden="true">
                      <span className={cls.cardEmoji}>{c.emoji}</span>
                    </div>
                  </div>

                  <div className={cls.gCardBody}>
                    <div className={cls.gCardTitle}>{c.title}</div>
                    <div className={cls.gCardDesc}>{c.desc}</div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>

        <div className={cls.bottomSpace} />
      </div>
    </main>
  );
}
